/**
 * Empty-state illustration for the Comments tab: a tilted speech bubble with a
 * smaller "someone's about to reply" bubble in front, plus the same playful
 * accents (squiggle, sparkles) as `EmptyDetailsIllustration`. Same drawing
 * language as that one — neutral surfaces for the main shapes, `--accent` gold for
 * the hero outline, `--primary` green for a sparkle — via inline `style`
 * (presentation attributes don't resolve `var()`), so it follows light/dark
 * theming.
 */
const EmptyCommentsIllustration = () => (
  <svg
    viewBox="0 0 200 160"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: '100%', height: 'auto', display: 'block' }}
    aria-hidden="true"
  >
    {/* Decorative rounded square, tilted, peeking from behind the bubble. */}
    <rect
      x="46"
      y="18"
      width="44"
      height="44"
      rx="13"
      transform="rotate(-10 68 40)"
      style={{ fill: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}
    />

    {/* Hand-drawn squiggle flourish, lower-left. */}
    <path
      d="M20 126 Q29 116 38 126 T56 126"
      fill="none"
      style={{
        stroke: 'color-mix(in srgb, var(--text-muted) 60%, transparent)',
      }}
      strokeWidth="3"
      strokeLinecap="round"
    />

    {/* Speech bubble with a tail, three lines of "text" — tilted for energy. */}
    <g transform="rotate(-5 104 67)">
      <path
        d="M62 32 H146 Q156 32 156 42 V92 Q156 102 146 102 H92 L76 118 V102 H62 Q52 102 52 92 V42 Q52 32 62 32 Z"
        style={{ fill: 'var(--surface)', stroke: 'var(--border)' }}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <g
        style={{
          stroke: 'color-mix(in srgb, var(--text-muted) 50%, transparent)',
        }}
        strokeWidth="3"
        strokeLinecap="round"
      >
        <line x1="68" y1="52" x2="140" y2="52" />
        <line x1="68" y1="68" x2="140" y2="68" />
        <line x1="68" y1="84" x2="114" y2="84" />
      </g>
    </g>

    {/* Brand-green accent star, top-left. */}
    <path
      d="M26 35 Q26 44 35 44 Q26 44 26 53 Q26 44 17 44 Q26 44 26 35 Z"
      style={{ fill: 'var(--primary)' }}
    />

    {/* Sparkles for a bit of fun — gold top-right, green bottom-left. */}
    <path
      d="M176 34 Q176 42 184 42 Q176 42 176 50 Q176 42 168 42 Q176 42 176 34 Z"
      style={{ fill: 'var(--accent)' }}
    />
    <path
      d="M58 135 Q58 140 63 140 Q58 140 58 145 Q58 140 53 140 Q58 140 58 135 Z"
      style={{ fill: 'var(--primary)' }}
    />

    {/* Reply bubble in front, mid-typing — gold outline, over the big bubble. */}
    <path
      d="M120 92 H168 Q176 92 176 100 V124 Q176 132 168 132 H140 L128 144 V132 H120 Q112 132 112 124 V100 Q112 92 120 92 Z"
      style={{ fill: 'var(--background)', stroke: 'var(--accent)' }}
      strokeWidth="4"
      strokeLinejoin="round"
    />
    <g style={{ fill: 'var(--accent)' }}>
      <circle cx="130" cy="112" r="3.5" />
      <circle cx="144" cy="112" r="3.5" />
      <circle cx="158" cy="112" r="3.5" />
    </g>
  </svg>
);

export default EmptyCommentsIllustration;
